import { Common } from 'src/AppState/Common';
import { CasinoStoreApiProvider } from 'src/domains/casino/utils/adapters/CasinoStoreAdapter/ApiProviders/CasinoStoreApiProvider';
import { LegacyApiProvider } from 'src/domains/casino/utils/adapters/CasinoStoreAdapter/ApiProviders/LegacyApiProvider';
import {
    ApiProviderType,
    CollectionType,
    LegacyCategoriesAndGamesType,
    LegacyCreateCategoryResponseType,
    LegacyDeleteCategoryResponseType,
    LegacyEditCategoryParamsType,
    LegacyEditCategoryResponseType,
} from 'src/domains/casino/utils/adapters/CasinoStoreAdapter/types';
import { OpenapiCategoriesAndGamesStaffEditGameResponse200Type } from 'src/domains/casino/api/generated/categories-and-games/openapi_categories_and_games_staff_edit_game';
import { LegacyEditGameParamsType } from 'src/domains/casino/state/AppCasino/store/game-store/Game.store';
import { applyStudioNameOverride } from 'src/domains/casino/utils/utils';

export class CasinoStoreAdapter {
    private readonly api: ApiProviderType;

    public constructor(common: Common, casinoStoreEnabled: boolean) {
        this.api = casinoStoreEnabled ? new CasinoStoreApiProvider(common) : new LegacyApiProvider(common);
    }

    public async getCategoriesAndGames(collection: CollectionType): Promise<LegacyCategoriesAndGamesType> {
        const categoriesAndGames = await this.api.getCategoriesAndGames(collection);
        return {
            ...categoriesAndGames,
            games: categoriesAndGames.games.map((game) => ({
                ...game,
                studio_name: applyStudioNameOverride(game.studio_name),
            })),
        };
    }

    public async createCategory(
        position: number,
        name: string,
        collection: CollectionType
    ): Promise<LegacyCreateCategoryResponseType> {
        return this.api.createCategory(position, name, collection);
    }

    public async removeCategory(
        categoryId: number,
        collection: CollectionType
    ): Promise<LegacyDeleteCategoryResponseType> {
        return this.api.removeCategory(categoryId, collection);
    }

    public async editCategory(
        categoryId: number,
        categoryDetails: LegacyEditCategoryParamsType['requestBody']['category_details'],
        collection: CollectionType
    ): Promise<LegacyEditCategoryResponseType> {
        return this.api.editCategory(categoryId, categoryDetails, collection);
    }

    public async changeCategoryPosition(
        newOrder: number[],
        updatedCategory: { categoryId: number; newPosition: number },
        collection: CollectionType
    ): Promise<void> {
        return this.api.changeCategoryPosition(newOrder, updatedCategory, collection);
    }

    public async assignGameToCategory(gameId: number, categoryId: number, position: number): Promise<void> {
        return this.api.assignGameToCategory(gameId, categoryId, position);
    }

    public async deleteGameFromCategory(
        gameId: number,
        categoryIds: Array<string>,
        collection: CollectionType
    ): Promise<void> {
        return this.api.deleteGameFromCategory(gameId, categoryIds, collection);
    }

    public async deleteLiveGame(gameId: number): Promise<void> {
        return this.api.deleteLiveGame(gameId);
    }

    public async changeGamesOrder(
        categoryId: number,
        categoryDetails: LegacyEditCategoryParamsType['requestBody']['category_details'],
        collection: CollectionType
    ): Promise<void> {
        return this.api.changeGamesOrder(categoryId, categoryDetails, collection);
    }

    public editGame = async (
        game: LegacyEditGameParamsType
    ): Promise<OpenapiCategoriesAndGamesStaffEditGameResponse200Type> => {
        return this.api.editGame(game);
    };
}
